import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import classNames from 'classnames';
import usePageParams from 'hooks/usePageParams';
import useQueryParams from 'hooks/useQueryParams';
import { Loader } from 'ncoded-component-library';
import { DataRoomShareableLink } from 'models/ShareableLink';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';
import CreateShareableLinkProvider from 'router/subrouters/DataRoom/providers/CreateShareableLink';
import DataRoomContext from 'router/subrouters/DataRoom/providers/DataRoom/DataRoom.context';
import EditShareableLinkProvider from 'router/subrouters/DataRoom/providers/EditShareableLink';
import useRefetch from 'hooks/useRefetch';
import useUndoActions from 'hooks/useUndoActions';
import { getHasElementsInArray, getSortValue, sentryError } from 'utils';
import ShareableLinkContent from '../../subrouters/ShareableLinks/pages/ShareableLinks/components/ShareableLinkContent';
import { SORTING_VALUES } from '../../subrouters/ShareableLinks/pages/ShareableLinks/components/ShareableLinksTable/components/data';
import { useLockScrollOnReroute } from 'hooks/useLockScrollOnReroute';
import useBusEvent from 'hooks/useBusEvent';
import { CallbackBusType, StandardEventTypes } from 'modules/bus';
import { useTranslation } from 'react-i18next';
import PopNotificationWithUndo from 'components/PopNotificationWithUndo';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import SegmentedToggle from 'components/SegmentedToggle';
import BannerWrapper from 'components/BannerWrapper';
import DiscoveryFeatureCard from 'components/DiscoveryFeatureCard';
import { COLOR_VARIANT } from 'models/Colors';
import { THIRD_RELEASE_DISCOVERING_FEATURE } from 'data/discoveringFeature';
import { DATE_FORMAT_FULL, getFormattedDate } from 'dateUtils';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';

import './ShareableLinksContentContainer.styles.scss';

const LIMIT = '10';

type ShareableLinksContentContainerProps = {
  className?: string;
  showIsActiveToggle?: boolean;
};

const ShareableLinksContentContainer: React.FC<
  ShareableLinksContentContainerProps
> = (props) => {
  const { children, className, showIsActiveToggle = false } = props;

  const [isActive, setIsActive] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(0);
  const [shareableLinks, setShareableLinks] = useState<DataRoomShareableLink[]>(
    [],
  );
  const [lastToggledLink, setLastToggledLink] =
    useState<DataRoomShareableLink | null>(null);

  const {
    dataRoom: { _id: dataRoomId, hasSecureLinkActivity },
  } = useContext(DataRoomContext);
  const {
    currentUser: { seenSecureLinkAnalyticsFeature },
    updatePreferences,
  } = useContext(CurrentUserContext);
  const { popServerError } = useContext(PopNotificationsContext);

  const { isUndoAvailable, setUndoTimeout, clearUndoTimeout } =
    useUndoActions();

  const { refetch, refetchFlag } = useRefetch();

  const page = usePageParams();
  const { params, setQueryParam } = useQueryParams();

  const { sort } = params;

  useLockScrollOnReroute();

  const isEmpty = useMemo(
    () => !getHasElementsInArray(shareableLinks),
    [shareableLinks],
  );

  const showDiscoveryBanner = useMemo(
    () => !isEmpty && !seenSecureLinkAnalyticsFeature && hasSecureLinkActivity,
    [hasSecureLinkActivity, isEmpty, seenSecureLinkAnalyticsFeature],
  );

  const classes = classNames('shareable-links-content-container', className);

  const { t } = useTranslation();

  const updateShareableLink = useCallback(
    (shareableLink: DataRoomShareableLink) =>
      setShareableLinks((old) =>
        old.map((link) =>
          link?._id === shareableLink?._id ? shareableLink : link,
        ),
      ),
    [],
  );

  const handleAfterToggleState = useCallback(
    (linkBeforeToggle: DataRoomShareableLink) => {
      setLastToggledLink(linkBeforeToggle);
      setUndoTimeout(() => setLastToggledLink(null));
    },
    [setUndoTimeout],
  );

  const handleUndo = useCallback(async () => {
    if (!lastToggledLink) return;

    clearUndoTimeout();

    try {
      const { data } =
        await dataRoomApi.shareableLinks.changeShareableLinkState(
          lastToggledLink._id,
          lastToggledLink.isActive,
        );

      updateShareableLink({ ...lastToggledLink, ...data });
    } catch (e) {
      popServerError(e);
    } finally {
      setLastToggledLink(null);
    }
  }, [lastToggledLink, clearUndoTimeout, updateShareableLink, popServerError]);

  const updateShareableLinkFromEvent = useCallback(
    ({ payload }: CallbackBusType<DataRoomShareableLink>) => {
      if (!payload?._id) return;

      updateShareableLink(payload);
    },
    [updateShareableLink],
  );

  useBusEvent(
    StandardEventTypes.SHAREABLE_LINK_UPDATED,
    updateShareableLinkFromEvent,
  );

  useEffect(() => {
    if (!sort)
      setQueryParam(
        'sort',
        getSortValue(SORTING_VALUES.linkExpiresDate, false),
      );
  }, [sort, setQueryParam]);

  useEffect(() => {
    (async () => {
      if (!sort) return;

      setIsLoading(true);
      try {
        const {
          data: { items, totalPages: nextTotalPages },
        } = await dataRoomApi.shareableLinks.getShareableLinks(dataRoomId, {
          $page: page,
          $limit: LIMIT,
          $sort: sort,
          $relations: '["dataRoomNodes", "dataRoomNda"]',
          isActive,
        });

        setShareableLinks(items);
        setTotalPages(nextTotalPages);
      } catch (error) {
        sentryError(error);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [dataRoomId, page, sort, refetchFlag, isActive]);

  if (!sort) return null;

  return (
    <>
      {showIsActiveToggle && (
        <SegmentedToggle
          options={[
            {
              label: t('active'),
              value: true,
            },
            {
              label: t('inactive'),
              value: false,
            },
          ]}
          selectedValue={isActive}
          onChange={(value) => setIsActive(!!value)}
        />
      )}
      {showDiscoveryBanner && (
        <BannerWrapper
          className="shareable-links-content-container__banner"
          color={COLOR_VARIANT.PRIMARY}
          onClose={() =>
            updatePreferences({ seenSecureLinkAnalyticsFeature: true })
          }
        >
          <DiscoveryFeatureCard
            title={t('secureLinkAnalyticsBannerTitle')}
            description="secureLinkAnalyticsBannerDescription"
            descriptionValues={{
              trackingDate: getFormattedDate(
                new Date(THIRD_RELEASE_DISCOVERING_FEATURE),
                DATE_FORMAT_FULL,
              ),
            }}
          />
        </BannerWrapper>
      )}
      <div className={classes}>
        <CreateShareableLinkProvider onSuccessFullyCreated={refetch}>
          <EditShareableLinkProvider updateShareableLink={updateShareableLink}>
            {children && !isLoading && isEmpty ? (
              children
            ) : (
              <ShareableLinkContent
                isLoading={isLoading}
                isEmpty={isEmpty}
                isActive={isActive}
                totalPages={totalPages}
                shareableLinks={shareableLinks}
                updateShareableLink={updateShareableLink}
                onAfterToggleState={handleAfterToggleState}
              />
            )}
          </EditShareableLinkProvider>
        </CreateShareableLinkProvider>

        {isLoading && !isEmpty && <Loader />}
      </div>
      <PopNotificationWithUndo
        isUndoAvailable={isUndoAvailable}
        onUndo={handleUndo}
      >
        <p>
          {t(
            lastToggledLink?.isActive
              ? 'secureLinkInactiveMessage'
              : 'secureLinkActiveMessage',
          )}
        </p>
      </PopNotificationWithUndo>
    </>
  );
};

export default ShareableLinksContentContainer;
